import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { MdDeleteForever } from 'react-icons/md';
import { useTranslation } from 'react-i18next';
import '../../style/ManageLostPetImagesPage.css';

const ManageLostPetImagesPage = () => {
    const { id } = useParams(); // Get the pet ID from the URL
    const navigate = useNavigate(); // Initialize navigate hook
    const { t } = useTranslation();
    const [pet, setPet] = useState(null);
    const [newImages, setNewImages] = useState([]); // State for new image files
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';

    useEffect(() => {
        const fetchPet = async () => {
            try {
                const storedUser = JSON.parse(localStorage.getItem('user')); // Get the user from localStorage
                const token = storedUser?.token; // Extract the token

                if (!token) {
                    throw new Error(t('error.noAuthToken'));
                }

                const response = await axios.get(`${baseUrl}/api/pets/${id}`, {
                    headers: {
                        Authorization: `Bearer ${token}`, // Include the token in the Authorization header
                    },
                });

                setPet(response.data);
            } catch (err) {
                setError(err.message || t('error.fetchPetDetails'));
            }
        };

        fetchPet();
    }, [id, baseUrl, t]);

    const handleImageUpload = async () => {
        try {
            setLoading(true);
            const storedUser = JSON.parse(localStorage.getItem('user')); // Get the user from localStorage
            const token = storedUser?.token; // Extract the token

            if (!token) {
                throw new Error(t('error.noAuthToken'));
            }

            // Create a FormData object to handle file uploads
            const formData = new FormData();
            newImages.forEach((image) => {
                formData.append('images', image); // Append each image file
            });

            const response = await axios.put(`${baseUrl}/api/pets/${id}/lost-images`, formData, {
                headers: {
                    Authorization: `Bearer ${token}`, // Include the token in the Authorization header
                    'Content-Type': 'multipart/form-data', // Set the content type for file uploads
                },
            });

            setPet(response.data); // Update the pet data with the new images
            setNewImages([]); // Clear the new images state
            setLoading(false);

            // Redirect to PetPage.js
            navigate(`/pet/${id}`);
        } catch (err) {
            setError(err.message || t('error.uploadImages'));
            setLoading(false);
        }
    };

    const handleImageDelete = async (imageUrl) => {
        try {
            const storedUser = JSON.parse(localStorage.getItem('user')); // Get the user from localStorage
            const token = storedUser?.token; // Extract the token

            if (!token) {
                throw new Error(t('error.noAuthToken'));
            }

            const response = await axios.delete(`${baseUrl}/api/pets/${id}/images`, {
                headers: {
                    Authorization: `Bearer ${token}`, // Include the token in the Authorization header
                },
                data: { imageUrl }, // Pass the image URL to delete
            });

            setPet(response.data); // Update the pet data after deletion
        } catch (err) {
            setError(err.message || t('error.deleteImage'));
        }
    };

    const handleImageChange = (e) => {
        const files = Array.from(e.target.files);
        setNewImages(files); // Store the selected files in state
    };

    const renderImagePreviews = () => {
        return newImages.map((image, index) => (
            <div key={index} className="image-preview-container">
                <img
                    src={URL.createObjectURL(image)}
                    alt={t('manageLostPetImagesPage.previewAlt', { index: index + 1 })}
                />
            </div>
        ));
    };

    if (error) {
        return <div>{t('error.generalError', { error })}</div>;
    }

    if (!pet) {
        return <div>{t('loading')}</div>;
    }

    // Filter images to include only those tagged as 'lost'
    const lostImages = pet.images.filter((image) => image.tag === 'lost');

    return (
        <div className="manage-pet-images-page">
            <div className="manage-pet-images-header">
                <h1>{t('manageLostPetImagesPage.title', { name: pet.name })}</h1>
            </div>
            <div className="pet-images">
                {lostImages.map((image, index) => (
                    <div key={index} className="image-container">
                        <img
                            src={image.url}
                            alt={t('manageLostPetImagesPage.imageAlt', { index: index + 1 })}
                        />
                        <button onClick={() => handleImageDelete(image.url)} className="delete-icon">
                            <MdDeleteForever size={24} />
                        </button>
                    </div>
                ))}
            </div>
            <div className="upload-images">
                <h3>{t('manageLostPetImagesPage.uploadTitle')}</h3>
                <input type="file" accept="image/*" multiple onChange={handleImageChange} />
                <div className="image-previews">{renderImagePreviews()}</div>
                <button onClick={handleImageUpload} disabled={loading || newImages.length === 0}>
                    {loading ? t('manageLostPetImagesPage.uploading') : t('manageLostPetImagesPage.uploadButton')}
                </button>
            </div>
        </div>
    );
};

export default ManageLostPetImagesPage;
